// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const { FetchStateEnum } = require('client-v2/common/utils/enums')
const EmojiMentionsInput = require('client-v2/common/components/emoji-mentions-input-lazy')
const InputCheckbox = require('client-v2/common/components/input-checkbox')

const bem = BemEntity('ShareModalShareFriends')

const MAX_COMMENT_LENGTH = 200

function ShareModalShareFriends(props) {
  const { friends, friendsState, searchText, submitState, user } = props
  const isSubmitted = submitState !== FetchStateEnum.EMPTY
  if (submitState === FetchStateEnum.LOADED) {
    return tag(SuccessMessage)
  }
  if (friendsState === FetchStateEnum.LOADED && !searchText && !friends.length) {
    return tag(EmptyMessage, {
      onNavigate: props.onNavigate
    })
  }
  return (
    tag('div', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag(Header),
      tag(SearchInput, {
        value: searchText,
        onChange: props.onFriendsSearch
      }),
      tag(FriendsList, {
        disabled: isSubmitted,
        friends,
        isLoading: friendsState === FetchStateEnum.LOADING,
        onFriendClick: props.onFriendClick
      }),
      tag(MessageForm, {
        friends,
        isSubmitted,
        user,
        onSubmit: props.onFriendsShare
      })
    )
  )
}

ShareModalShareFriends.propTypes = {
  // Values
  className: PropTypes.string,
  friends: PropTypes.arrayOf(PropTypes.object).isRequired,
  friendsState: PropTypes.string.isRequired,
  searchText: PropTypes.string.isRequired,
  submitState: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  // Callbacks
  onFriendClick: PropTypes.func.isRequired,
  onFriendsSearch: PropTypes.func.isRequired,
  onFriendsShare: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired
}

module.exports = ShareModalShareFriends

// BEM Block entity Element components

function SuccessMessage() {
  return (
    tag('div', {
      className: bem.element('message')
    },
      'Shared successfully'
    )
  )
}

function EmptyMessage(props) {
  return (
    tag('div', {
      className: bem.block()
    },
      tag('div', {
        className: bem.element('message')
      },
        'You don’t have any friends to share with'
      ),
      tag(CapsuleButton, {
        className: bem.element('follow-button'),
        href: '/follow-people',
        onClick: props.onNavigate
      },
        'Follow friends'
      )
    )
  )
}

EmptyMessage.propTypes = {
  onNavigate: PropTypes.func.isRequired
}

function Header() {
  return (
    tag('div', {
      className: bem.element('message')
    },
      'Share with friends'
    )
  )
}

function FriendsList(props) {
  if (props.isLoading) {
    return (
      tag('div', {
        className: bem.element('loading-indicator')
      })
    )
  }
  const { friends } = props
  if (!friends.length) {
    return (
      tag('div', {
        className: bem.element('message')
      },
        'No friends found'
      )
    )
  }
  return (
    tag('div', {
      className: bem.element('friends-list')
    },
      friends.map(friend => (
        tag(Friend, {
          key: `share-friend:${friend.id}`,
          disabled: props.disabled,
          friend,
          onClick: props.onFriendClick
        })
      ))
    )
  )
}

FriendsList.propTypes = {
  // Values
  disabled: PropTypes.bool.isRequired,
  friends: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  // Callbacks
  onFriendClick: PropTypes.func.isRequired
}

function Friend(props) {
  const { friend } = props
  return (
    tag('div', {
      className: bem.element('friend'),
      onClick: () => props.onClick(friend.id)
    },
      tag(UserAvatar, {
        className: bem.element('friend-icon'),
        href: true,
        user: friend
      }),
      tag('div', {
        className: bem.element('friend-content')
      },
        tag('div', {
          className: bem.element('friend-name')
        },
          friend['display_name']
        ),
        tag('div', {
          className: bem.element('friend-username')
        },
          `@${friend.username}`
        )
      ),
      tag(InputCheckbox, {
        className: bem.element('friend-checkbox'),
        checked: friend.isSelected,
        disabled: props.disabled,
        readOnly: true
      })
    )
  )
}

Friend.propTypes = {
  // Values
  disabled: PropTypes.bool,
  friend: PropTypes.object.isRequired,
  // Callbacks
  onClick: PropTypes.func.isRequired
}

function SearchInput(props) {
  return (
    tag('input', {
      className: bem.element('search-input'),
      placeholder: 'Search friends...',
      onChange: event => props.onChange(event.target.value),
      value: props.value
    })
  )
}

SearchInput.propTypes = {
  // Values
  value: PropTypes.string.isRequired,
  // Callbacks
  onChange: PropTypes.func.isRequired
}

function MessageForm(props) {
  const [ messageText, setMessageText ] = React.useState('')

  const onReset = event => {
    event.preventDefault()
    setMessageText('')
  }

  const onSubmit = event => {
    event.preventDefault()
    props.onSubmit(messageText)
    setMessageText('')
  }

  const onInputChange = (event, newValue, newPlainTextValue) => {
    setMessageText(newPlainTextValue)
  }

  const selectedFriends = props.friends.filter(f => f.isSelected)
  const selectedCount = selectedFriends.length

  const submitLabelText = () => {
    if (props.isSubmitted) {
      return 'Sharing...'
    }
    if (!selectedCount) {
      return 'Select friends'
    }
    if (selectedCount === 1) {
      const [ friend ] = selectedFriends
      return `Share privately with ${friend['display_name']}`
    }
    return `Share privately with ${selectedCount} people`
  }

  return (
    tag('form', {
      className: bem.element('form'),
      onReset,
      onSubmit
    },
      tag(UserAvatar, {
        className: bem.element('form-icon'),
        badge: false,
        user: props.user,
        visible: true
      }),
      tag(EmojiMentionsInput, {
        disabled: props.isSubmitted,
        maxLength: MAX_COMMENT_LENGTH,
        placeholder: 'Add a private message...',
        popperPlacement: 'top',
        value: messageText,
        onChange: onInputChange
      }),
      tag(CapsuleButton, {
        className: bem.element('form-submit'),
        type: 'submit',
        disabled: selectedCount === 0
      },
        submitLabelText()
      )
    )
  )
}

MessageForm.propTypes = {
  // Values
  friends: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  // Callbacks
  onSubmit: PropTypes.func.isRequired
}
